import { EllipsisAutoPositionTooltipSpan, GetColor, TooltipBodyDirection } from 'venn-ui-kit';
import React, { type ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import type { FrequencyEnum, OptionData, SubjectRangeAnalysis } from 'venn-api';
import { assertExhaustive, type HistoricalPortfolioNodeAllocationType } from 'venn-utils';
import { AllocationItemHistoricalTooltipContent } from './AllocationItemHistoricalTooltipContent';
import { StyledLabel, StyledSymbol } from './allocation-item-name-tooltip-shared';
import { OptionTooltipContent } from '../common/OptionTooltipContent';

type AllocationItemConfig =
  | {
      allocationPanelType: 'historical read-only';
      _tag: 'fund';
      rangeAnalysis: SubjectRangeAnalysis;
    }
  | {
      allocationPanelType: 'historical read-only';
      _tag: 'strategy';
    }
  | {
      allocationPanelType: 'full';
      _tag: 'strategy';
    }
  | {
      allocationPanelType: 'full';
      _tag: 'fund';
    };

export type AllocationItemNameTooltipProps = {
  originalName: string | undefined;
  name: string;
  isGhost: boolean;
  allocationItemConfig: AllocationItemConfig;
  isSelected: boolean;
  isOutsideOfSelectedSubtree: boolean;
  investmentTimeRange: string | undefined;
  isMissingReturnsMessage: JSX.Element | undefined;
  investmentSymbol: string | undefined;
  hasAddFund: boolean;
  historicalPortfolioNodeAllocationType: HistoricalPortfolioNodeAllocationType | undefined;
  optionData: OptionData | undefined;
  frequency: FrequencyEnum | undefined;
  children?: ReactNode;
};

type BasicTooltipContentProps = {
  investmentSymbol: string | undefined;
  contentHeader: string;
  isMissingReturnsMessage: React.ReactNode;
};

const BasicTooltipContent = ({
  investmentSymbol,
  contentHeader,
  isMissingReturnsMessage,
}: BasicTooltipContentProps) => {
  return (
    <TooltipContent>
      <div>
        {investmentSymbol ? <StyledSymbol>{investmentSymbol}</StyledSymbol> : null}
        <StyledLabel inverted>{contentHeader}</StyledLabel>
      </div>
      {isMissingReturnsMessage}
    </TooltipContent>
  );
};

export const AllocationItemNameTooltip = ({
  originalName,
  name,
  isOutsideOfSelectedSubtree,
  isSelected,
  investmentTimeRange,
  isMissingReturnsMessage,
  allocationItemConfig: item,
  isGhost,
  investmentSymbol,
  historicalPortfolioNodeAllocationType,
  optionData,
  frequency,
  children,
}: AllocationItemNameTooltipProps) => {
  const isStrategy = item._tag === 'strategy';
  const hasChanged = !!originalName && name !== originalName;
  // If it's an investment, we will always show tooltip for adding investment time range
  const alwaysShowTooltip = !isStrategy && !isGhost;
  const contentHeader = investmentTimeRange ? `${name} (${investmentTimeRange})` : name;

  const tooltipContent = useMemo(() => {
    const basicTooltipContent = (
      <BasicTooltipContent
        investmentSymbol={investmentSymbol}
        contentHeader={contentHeader}
        isMissingReturnsMessage={isMissingReturnsMessage}
      />
    );

    // Missing returns message takes precedence over available analysis range
    if (isMissingReturnsMessage) {
      return basicTooltipContent;
    }

    if (optionData) {
      return (
        <OptionTooltipContent
          name={name}
          optionData={optionData}
          investmentTimeRange={investmentTimeRange}
          investmentSymbol={investmentSymbol}
          frequency={frequency}
        />
      );
    }

    switch (item.allocationPanelType) {
      case 'historical read-only':
        switch (item._tag) {
          case 'fund':
            return (
              <AllocationItemHistoricalTooltipContent
                investmentSymbol={investmentSymbol}
                contentHeader={name}
                rangeAnalysis={{
                  historicalStart: item.rangeAnalysis.historicalStart,
                  historicalEnd: item.rangeAnalysis.historicalEnd,
                  frequency: item.rangeAnalysis.frequency,
                }}
                historicalPortfolioNodeAllocationType={historicalPortfolioNodeAllocationType}
              />
            );
          case 'strategy':
            return basicTooltipContent;
          default:
            return assertExhaustive(item, `Unexpected allocation item type: ${item}`);
        }
      case 'full':
        return basicTooltipContent;
      default:
        return assertExhaustive(item, `Unexpected allocation item type: ${item}`);
    }
  }, [
    investmentSymbol,
    contentHeader,
    isMissingReturnsMessage,
    optionData,
    item,
    name,
    investmentTimeRange,
    frequency,
    historicalPortfolioNodeAllocationType,
  ]);

  return (
    <EllipsisAutoPositionTooltipSpan
      usePortal
      content={tooltipContent}
      bodyDirection={TooltipBodyDirection.Right}
      alwaysShowTooltip={alwaysShowTooltip}
      tooltipMaxWidth={172}
    >
      <NameContainer>
        <Name
          hasChanged={hasChanged}
          isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
          isSelected={isSelected}
          data-testid="qa-allocation-item-name"
        >
          {name}
        </Name>
        <ChildrenContainer>{children}</ChildrenContainer>
      </NameContainer>
    </EllipsisAutoPositionTooltipSpan>
  );
};

const TooltipContent = styled.div`
  > div:last-child {
    :not(:only-child) {
      margin-top: 5px;
    }
  }
`;

const Name = styled.span<{ hasChanged: boolean; isOutsideOfSelectedSubtree: boolean; isSelected: boolean }>`
  line-height: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ hasChanged, isOutsideOfSelectedSubtree, isSelected }) =>
    hasChanged && !isOutsideOfSelectedSubtree && !isSelected
      ? GetColor.HighlightDark
      : hasChanged && isOutsideOfSelectedSubtree && !isSelected
        ? GetColor.HighlightLight
        : undefined};
`;

const NameContainer = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ChildrenContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  white-space: nowrap;
`;
